import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Trash2, Crown, BadgeCheck, Ban, Unlock, Download, Megaphone, Star, MessageSquare, Send } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader, UserAvatar, Pill } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { PushCard } from "@/components/PushCard";
import { timeAgo } from "@/lib/labels";

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB") : "—";

function Stats() {
  const api = useApi();
  const [s, setS] = useState(null);
  useEffect(() => { api("get", "/admin/stats").then(setS); }, [api]);
  if (!s) return <Loader />;
  const items = [["Technicians", s.technicians], ["Companies", s.companies], ["New users (7d)", s.new_users_7d], ["VIP active", s.vip_active],
    ["Paying subscribers", s.paying_subscribers], ["Active jobs", s.jobs_active], ["Applications", s.applications], ["Messages (7d)", s.messages_7d], ["Connections", s.connections], ["Posts (7d)", s.posts_7d], ["Open reports", s.open_reports], ["Blocked users", s.blocked_users]];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {items.map(([l, v]) => (
        <div key={l} className="panel p-5" data-testid={`admin-stat-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <p className="eyebrow">{l}</p><p className="mt-2 text-3xl font-black">{v}</p>
        </div>
      ))}
    </div>
  );
}

function Users() {
  const api = useApi();
  const { me } = useMe();
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const load = useCallback(() => api("get", `/admin/users?q=${encodeURIComponent(q)}`).then(setData), [api, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const act = async (method, url, body, msg) => {
    try { await api(method, url, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const block = (u) => {
    const reason = window.prompt(`Reason for blocking ${u.name}:`);
    if (reason && reason.trim().length >= 2) act("post", `/admin/users/${u.user_id}/block`, { reason }, "User blocked");
  };
  const remove = (u) => {
    const typed = window.prompt(`This permanently deletes ${u.name}, their profile, jobs, posts and messages. Type DELETE to confirm.`);
    if (typed === "DELETE") act("delete", `/admin/users/${u.user_id}`, undefined, "Account deleted");
  };
  if (!data) return <Loader />;
  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="relative max-w-md flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="admin-users-search" className="pl-9 bg-white" placeholder="Search name, email, city..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <ExportButton url="/admin/export/users.csv" name="windworkhub-users.csv" testId="admin-export-users" />
          <ExportButton url="/admin/export/backup.json" name="windworkhub-backup.json" testId="admin-export-backup" label="Full backup" />
        </div>
      </div>
      <div className="mt-4 panel overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Type</TableHead><TableHead>VIP</TableHead><TableHead>Access</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.users.map((u) => (
              <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className={u.blocked ? "bg-red-50/60" : ""}>
                <TableCell>
                  <Link to={`/u/${u.user_id}`} className="flex items-center gap-3 min-w-[200px]">
                    <UserAvatar user={u} size={34} />
                    <div>
                      <p className="font-semibold flex items-center gap-1.5">{u.name}{u.verified && <BadgeCheck size={15} className="text-sky-600" />}</p>
                      <p className="text-xs text-slate-500">{u.email || (u.demo ? "demo profile" : "")}</p>
                      {u.blocked && <p className="text-xs text-red-700" data-testid={`admin-user-blocked-${u.user_id}`}>Blocked: {u.blocked_reason}</p>}
                    </div>
                  </Link>
                </TableCell>
                <TableCell><Pill>{u.role || "no role"}</Pill></TableCell>
                <TableCell className="whitespace-nowrap">
                  {u.is_vip ? <Pill cls="bg-amber-50 text-amber-800 border-amber-200"><Crown size={11} />{u.access_role === "admin" ? "admin" : `${u.vip_source || ""} · ${fmt(u.vip_until)}`}</Pill> : <Pill>none</Pill>}
                </TableCell>
                <TableCell>
                  <Select value={u.access_role} disabled={u.user_id === me.user_id} onValueChange={(v) => act("patch", `/admin/users/${u.user_id}/role`, { role: v }, "Access updated")}>
                    <SelectTrigger className="w-[120px] h-8" data-testid={`admin-role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap space-x-1">
                  {u.user_id !== me.user_id && u.role && <Button size="sm" variant="outline" asChild><Link to={`/messages/${u.user_id}`} data-testid={`admin-message-${u.user_id}`}><MessageSquare size={13} />Message</Link></Button>}
                  <Button size="sm" variant="outline" onClick={() => act("patch", `/admin/users/${u.user_id}/vip`, { action: "grant", days: 30 }, "VIP +30 days")} data-testid={`admin-vip-30-${u.user_id}`}>+30d</Button>
                  <Button size="sm" variant="outline" onClick={() => act("patch", `/admin/users/${u.user_id}/vip`, { action: "grant", days: 90 }, "VIP +90 days")} data-testid={`admin-vip-90-${u.user_id}`}>+90d</Button>
                  <Button size="sm" variant="ghost" onClick={() => act("patch", `/admin/users/${u.user_id}/vip`, { action: "revoke" }, "VIP revoked")} data-testid={`admin-vip-revoke-${u.user_id}`}>Revoke</Button>
                  {u.access_role !== "admin" && (u.blocked
                    ? <Button size="sm" variant="outline" onClick={() => act("delete", `/admin/users/${u.user_id}/block`, undefined, "User unblocked")} data-testid={`admin-unblock-${u.user_id}`}><Unlock size={13} />Unblock</Button>
                    : <Button size="sm" variant="outline" className="text-red-700" onClick={() => block(u)} data-testid={`admin-block-${u.user_id}`}><Ban size={13} />Block</Button>)}
                  {u.access_role !== "admin" && <Button size="icon" variant="ghost" className="text-red-700 h-8 w-8" onClick={() => remove(u)} data-testid={`admin-delete-user-${u.user_id}`} aria-label="Delete account"><Trash2 size={14} /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ExportButton({ url, name, testId, label = "Export CSV" }) {
  const api = useApi();
  const download = async () => {
    try {
      const blob = await api("get", url, null, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return <Button variant="outline" size="sm" onClick={download} data-testid={testId}><Download size={14} />{label}</Button>;
}

function ActivityLog() {
  const api = useApi();
  const [list, setList] = useState(null);
  useEffect(() => { api("get", "/admin/log").then(setList); }, [api]);
  if (!list) return <Loader />;
  if (!list.length) return <p className="text-sm text-slate-500" data-testid="admin-log-empty">No admin actions yet.</p>;
  return (
    <div className="panel divide-y divide-slate-100">
      {list.map((l) => (
        <div key={l.id} className="p-3 text-sm flex flex-wrap gap-x-3 gap-y-1" data-testid={`admin-log-row-${l.id}`}>
          <span className="font-mono text-xs text-slate-400 w-36">{new Date(l.created_at).toLocaleString("en-GB")}</span>
          <span className="font-semibold">{l.admin_name}</span>
          <span className="font-mono text-xs bg-slate-100 px-1.5 rounded">{l.action}</span>
          {(l.target_name || l.target_id) && <span className="text-slate-600">{l.target_name || l.target_id}</span>}
          {l.details && <span className="text-slate-500">— {l.details}</span>}
        </div>
      ))}
    </div>
  );
}

const AUDIENCE = { all: "all users", technician: "technicians", company: "companies" };

function BroadcastForm() {
  const api = useApi();
  const [text, setText] = useState("");
  const [email, setEmail] = useState(false);
  const [audience, setAudience] = useState("all");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!window.confirm(`Send this private message to ${AUDIENCE[audience].toUpperCase()}${email ? " and by email" : ""}?`)) return;
    setBusy(true);
    try { const r = await api("post", "/admin/broadcast", { text, email, audience }); toast.success(`Sent to ${r.sent} ${AUDIENCE[audience]}${email ? `, ${r.emailed} emails` : ""}`); setText(""); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="panel p-6 max-w-lg space-y-3" data-testid="admin-broadcast-form">
      <Label>Private message to</Label>
      <Select value={audience} onValueChange={setAudience}>
        <SelectTrigger data-testid="admin-broadcast-audience"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid="admin-broadcast-audience-all">All users</SelectItem>
          <SelectItem value="technician" data-testid="admin-broadcast-audience-technician">Technicians only</SelectItem>
          <SelectItem value="company" data-testid="admin-broadcast-audience-company">Companies only</SelectItem>
        </SelectContent>
      </Select>
      <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Arrives in their Messages inbox from you" data-testid="admin-broadcast-text" />
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={email} onCheckedChange={(v) => setEmail(!!v)} data-testid="admin-broadcast-email" />Also send by email (to users with email notifications on)</label>
      <Button onClick={send} disabled={busy || text.trim().length < 2} data-testid="admin-broadcast-send"><Send size={14} />{busy ? "Sending..." : `Send to ${AUDIENCE[audience]}`}</Button>
    </div>
  );
}

function AnnouncementForm() {
  const api = useApi();
  const [a, setA] = useState(null);
  useEffect(() => { api("get", "/admin/announcement").then((d) => setA((cur) => cur || d)); }, [api]);
  if (!a) return null;
  const save = async (active) => {
    try { setA(await api("put", "/admin/announcement", { text: a.text, active })); toast.success(active ? "Announcement published" : "Announcement hidden"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-6 max-w-lg space-y-3 mt-4" data-testid="admin-announcement-form">
      <Label>Announcement for all users</Label>
      <Textarea rows={3} value={a.text} onChange={(e) => setA({ ...a, text: e.target.value })} placeholder="e.g. New: Community groups are live!" data-testid="admin-announcement-text" />
      <p className="text-xs text-slate-500">Status: {a.active ? "visible to everyone" : "hidden"}</p>
      <div className="flex gap-2">
        <Button onClick={() => save(true)} disabled={!a.text.trim()} data-testid="admin-announcement-publish"><Megaphone size={14} />Publish</Button>
        {a.active && <Button variant="outline" onClick={() => save(false)} data-testid="admin-announcement-hide">Hide</Button>}
      </div>
    </div>
  );
}

function Jobs() {
  const api = useApi();
  const [jobs, setJobs] = useState(null);
  const load = useCallback(() => api("get", "/admin/jobs").then(setJobs), [api]);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => {
    if (!window.confirm("Delete this job and its applications?")) return;
    try { await api("delete", `/admin/jobs/${id}`); toast.success("Job deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!jobs) return <Loader />;
  return (
    <div className="panel divide-y divide-slate-100">
      {jobs.map((j) => (
        <div key={j.id} className="p-4 flex items-center gap-4" data-testid={`admin-job-row-${j.id}`}>
          <div className="flex-1 min-w-0">
            <Link to={`/jobs/${j.id}`} className="font-semibold hover:text-sky-700">{j.title}</Link>
            <p className="text-xs text-slate-500">{j.company_name} · {j.applicants_count} applicants · {timeAgo(j.created_at)} {j.active ? "" : "· inactive"}</p>
          </div>
          <Button size="sm" variant={j.featured ? "default" : "outline"} className={j.featured ? "bg-amber-400 text-slate-950 hover:bg-amber-300" : ""}
            onClick={() => api("put", `/admin/jobs/${j.id}/featured`, { featured: !j.featured }).then(() => { toast.success(j.featured ? "Unfeatured" : "Featured"); load(); }).catch((e) => toast.error(errMsg(e)))}
            data-testid={`admin-job-feature-${j.id}`}><Star size={14} />{j.featured ? "Featured" : "Feature"}</Button>
          <Button size="icon" variant="outline" onClick={() => remove(j.id)} data-testid={`admin-job-delete-${j.id}`} aria-label="Delete"><Trash2 size={15} /></Button>
        </div>
      ))}
    </div>
  );
}

function Settings() {
  const api = useApi();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api("get", "/admin/settings").then((d) => setS((cur) => cur ?? d)); }, [api]);
  if (!s) return <Loader />;
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { setS(await api("put", "/admin/settings", { vip_price_eur: Number(s.vip_price_eur), trial_days: Number(s.trial_days) })); toast.success("Settings saved"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="panel p-6 max-w-lg space-y-5" data-testid="admin-settings-form">
      <div className="space-y-1.5"><Label>VIP price (EUR / month)</Label>
        <Input type="number" min={1} step="0.5" value={s.vip_price_eur} onChange={(e) => setS({ ...s, vip_price_eur: e.target.value })} data-testid="admin-settings-price" />
        <p className="text-xs text-slate-500">Applies to new subscriptions. Existing subscribers keep their price.</p></div>
      <div className="space-y-1.5"><Label>Free VIP for new users (days)</Label>
        <Input type="number" min={0} value={s.trial_days} onChange={(e) => setS({ ...s, trial_days: e.target.value })} data-testid="admin-settings-trial" />
        <p className="text-xs text-slate-500">Launch offer: 90 days. Set to 0 to end the free period for new sign-ups.</p></div>
      <Button type="submit" disabled={busy} data-testid="admin-settings-save">{busy ? "Saving..." : "Save settings"}</Button>
    </form>
  );
}

function SampleData() {
  const api = useApi();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api("get", "/admin/sample-data").then(setS), [api]);
  useEffect(() => { load(); }, [load]);
  const run = async (method, msg) => {
    setBusy(true);
    try { await api(method, "/admin/sample-data"); toast.success(msg); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="panel p-6 max-w-lg space-y-3 mt-6" data-testid="admin-sample-data">
      <p className="font-semibold">Example content</p>
      <p className="text-sm text-slate-600">28 example profiles (20 technicians, 8 companies from Europe, the US, Australia, Asia and South America), 17 jobs with photos and a few community posts. Only admins can see they are examples.</p>
      {s && <p className="text-sm font-mono" data-testid="admin-sample-data-count">Now: {s.users} profiles · {s.jobs} jobs</p>}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => run("post", "Example content loaded")} disabled={busy} data-testid="admin-sample-load">{busy ? "Working..." : "Load example content"}</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="outline" disabled={busy || !s?.users} data-testid="admin-sample-remove">Remove example content</Button></AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Remove example content?</AlertDialogTitle>
              <AlertDialogDescription>All example profiles, their jobs, posts and contacts will be deleted. Real users are not affected.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel data-testid="admin-sample-remove-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => run("delete", "Example content removed")} data-testid="admin-sample-remove-confirm">Remove</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function Payments() {
  const api = useApi();
  const [list, setList] = useState(null);
  useEffect(() => { api("get", "/admin/payments").then(setList); }, [api]);
  if (!list) return <Loader />;
  if (!list.length) return <p className="text-sm text-slate-500" data-testid="admin-payments-empty">No payments yet.</p>;
  return (
    <div className="space-y-3">
    <div className="flex justify-end"><ExportButton url="/admin/export/payments.csv" name="windworkhub-payments.csv" testId="admin-export-payments" /></div>
    <div className="panel overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>User</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>{list.map((t) => (
          <TableRow key={t.session_id} data-testid={`admin-payment-row-${t.session_id}`}>
            <TableCell>{fmt(t.created_at)}</TableCell><TableCell>{t.user_name}</TableCell>
            <TableCell>{t.amount} {t.currency?.toUpperCase()}</TableCell><TableCell><Pill>{t.payment_status}</Pill></TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
    </div>
    </div>
  );
}

function Reports() {
  const api = useApi();
  const [list, setList] = useState(null);
  const load = useCallback(() => api("get", "/admin/reports").then(setList), [api]);
  useEffect(() => { load(); }, [load]);
  const resolve = async (id, action) => {
    if (action === "delete" && !window.confirm("Delete the reported content?")) return;
    try { await api("post", `/admin/reports/${id}/resolve`, { action }); toast.success(action === "delete" ? "Content deleted" : "Report dismissed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!list) return <Loader />;
  if (!list.length) return <p className="text-sm text-slate-500" data-testid="admin-reports-empty">No open reports.</p>;
  return (
    <div className="space-y-3">
      {list.map((r) => (
        <div key={r.id} className="panel p-4" data-testid={`admin-report-row-${r.id}`}>
          <p className="eyebrow">{r.target_type} by {r.author_name || "deleted"} · reported by {r.reporter_name} · {timeAgo(r.created_at)}</p>
          {r.target?.title && <p className="mt-2 font-bold">{r.target.title}</p>}
          <p className="mt-1 text-sm text-slate-700 whitespace-pre-line">{r.target?.text || "(content already removed)"}</p>
          {r.reason && <p className="mt-2 text-sm text-amber-800 bg-amber-50 border-l-2 border-amber-400 px-3 py-1">Reason: {r.reason}</p>}
          <div className="mt-3 flex gap-2">
            <Button size="sm" variant="destructive" onClick={() => resolve(r.id, "delete")} data-testid={`admin-report-delete-${r.id}`}>Delete content</Button>
            <Button size="sm" variant="outline" onClick={() => resolve(r.id, "dismiss")} data-testid={`admin-report-dismiss-${r.id}`}>Dismiss</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Groups() {
  const api = useApi();
  const [groups, setGroups] = useState(null);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const load = useCallback(() => api("get", "/community/groups").then(setGroups), [api]);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    try { await api("post", "/admin/groups", { name, description: desc }); setName(""); setDesc(""); toast.success("Group created"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this group? Its posts stay in the main feed.")) return;
    try { await api("delete", `/admin/groups/${id}`); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!groups) return <Loader />;
  return (
    <div className="space-y-4 max-w-2xl">
      <form onSubmit={create} className="panel p-4 flex flex-wrap gap-2" data-testid="admin-group-form">
        <Input className="flex-1 min-w-[160px]" placeholder="Group name" value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-group-name" />
        <Input className="flex-[2] min-w-[200px]" placeholder="Description" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="admin-group-desc" />
        <Button type="submit" disabled={name.trim().length < 2} data-testid="admin-group-create">Create</Button>
      </form>
      <div className="panel divide-y divide-slate-100">
        {groups.map((g) => (
          <div key={g.id} className="p-4 flex items-center gap-3" data-testid={`admin-group-row-${g.id}`}>
            <div className="flex-1"><p className="font-semibold">{g.name}</p><p className="text-xs text-slate-500">{g.members_count} members · {g.posts_count} posts</p></div>
            <Button size="icon" variant="outline" onClick={() => remove(g.id)} data-testid={`admin-group-delete-${g.id}`} aria-label="Delete"><Trash2 size={15} /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  return (
    <div>
      <p className="eyebrow">Administration</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">Admin panel</h1>
      <Tabs defaultValue="overview" className="mt-8">
        <TabsList className="flex-wrap h-auto">
          {["overview", "users", "message", "jobs", "reports", "groups", "payments", "activity", "settings"].map((t) => <TabsTrigger key={t} value={t} className="capitalize" data-testid={`admin-tab-${t}`}>{t}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview" className="mt-6"><Stats /><PushCard /></TabsContent>
        <TabsContent value="users" className="mt-6"><Users /></TabsContent>
        <TabsContent value="message" className="mt-6"><BroadcastForm /></TabsContent>
        <TabsContent value="activity" className="mt-6"><ActivityLog /></TabsContent>
        <TabsContent value="jobs" className="mt-6"><Jobs /></TabsContent>
        <TabsContent value="reports" className="mt-6"><Reports /></TabsContent>
        <TabsContent value="groups" className="mt-6"><Groups /></TabsContent>
        <TabsContent value="payments" className="mt-6"><Payments /></TabsContent>
        <TabsContent value="settings" className="mt-6"><Settings /><AnnouncementForm /><SampleData /></TabsContent>
      </Tabs>
    </div>
  );
}
